import React, { useState } from 'react';  
import { Button, TextField, Link, Box, Grid, Typography, CircularProgress } from '@mui/material';
import { useForm, Controller } from "react-hook-form";
import { sendSignUpOTP, registerUser, validateSignUpOTP } from 'core/services/auth';
import { useNavigate, Link as RouteLink } from "react-router-dom";
import { useSnackbar } from 'notistack';
import LoginIcon from '@mui/icons-material/Login';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export default function Register() {
  const defaultValues =  {
    firstName: '',
    lastName:'',
    mobile:'',
    email:'',
    otp: '',
  };
  /* Hooks */
  const { enqueueSnackbar } = useSnackbar();
  let navigate = useNavigate();
  const [isSiningUp, setSiningUp] = useState(false);
  const [isOTPRequested, setIsOTPRequested] = useState(false);
  const [isOTPVerified, setIsOTPVerified] = useState(false);
  const { handleSubmit, control, formState: { errors }, reset } = useForm({defaultValues});

  const resetSignupForm = () => {
    reset({...defaultValues});
    setSiningUp(false);
    setIsOTPRequested(false);
    setIsOTPVerified(false);
  };
 
  const signupUser = (data) => {
    setSiningUp(true);
    registerUser(data).then(({ response, error }) => {
      setSiningUp(false);
      if(response){
        resetSignupForm();
        navigate('/signin');
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  const sendOTP = (data) => {
    sendSignUpOTP({mobile: data?.mobile}).then(({ response, error }) => {
      if(response){
        setIsOTPRequested(true);
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  };

  const validateOTP = (data) => {
    validateSignUpOTP({ mobile: data?.mobile, otp: data?.otp }).then(({ response, error }) => {
      if(response){
        setIsOTPVerified(true);
        signupUser(data);
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  };

  const onSubmit = (data) => {
    if(isOTPRequested){
      validateOTP(data);
    }else if(isOTPVerified) {
      signupUser(data);
    } else {
      sendOTP(data);
    }
  };

  return (
    <Grid
      sx={{
        mx: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Typography component="h1" variant="h5">
        Sign Up
      </Typography>
      <Grid item xs={9} sm={9} md={9} lg={9}>
        <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
            <Grid container columnSpacing={2} rowSpacing={0}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <Controller
                  control={control}
                  name="firstName"
                  id="firstName"
                  rules={{ required: "Enter your first name"}}
                  render={({field }) => (
                    <TextField
                      {...field}
                      margin="normal"
                      fullWidth
                      autoFocus
                      label="First Name"
                      required
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                      disabled={isOTPRequested}
                    />
                  )}
                />
              </Grid> 
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <Controller
                  control={control}
                  name="lastName"
                  id="lastName"
                  rules={{ required: "Enter your last name"}}
                  render={({field }) => (
                    <TextField
                      {...field}
                      margin="normal"
                      fullWidth
                      label="Last Name"
                      required
                      error={!!errors.lastName}
                      helperText={errors?.lastName?.message}
                      disabled={isOTPRequested}
                    />
                  )}
                />
              </Grid> 
              <Grid item xs={12} sm={12} md={12} lg={12}>
              <Controller
                control={control}
                name="mobile"
                id="mobile"
                rules={{ required: "Enter your mobile",
                  pattern: {
                    value: /^[0-9]{10}$/i,
                    message: "Enter a valid 10 digit mobile number",
                  },
                  // maxLength: 10,
                }}
                render={({field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    fullWidth
                    label="Mobile Number"
                    required
                    error={!!errors.mobile}
                    helperText={errors?.mobile?.message}
                    disabled={isOTPRequested}
                  />
                )}
              />
            </Grid> 
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Controller
                control={control}
                name="email"
                id="email"
                rules={{ required: "Enter email address",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                    message: "Enter a valid email address",
                  }, 
                }}
                render={({field}) => (
                  <TextField
                    {...field}
                    margin="normal"
                    fullWidth
                    label="Email"
                    type="text"
                    required
                    error={!!errors?.email}
                    helperText={errors.email?.message}
                    disabled={isOTPRequested}
                  />
                )}
              />
            </Grid>
            {isOTPRequested ? (
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <Controller
                  control={control}
                  name="otp"
                  id="otp"
                  rules={{ required: "Enter the 4-digit PIN sent to your registered mobile number",
                    pattern: {
                      value: /^[0-9]{4}$/i,
                      message: "Enter a valid 4 digit PIN",
                    },
                  }}
                  render={({field}) => (
                    <TextField
                      {...field}
                      margin="normal"
                      fullWidth
                      label="OTP"
                      type="text"
                      required
                      error={!!errors.otp}
                      helperText={errors.otp?.message}
                      disabled={isOTPVerified}
                    />
                  )}
                />
              </Grid>
            ): null} 
          </Grid>
          <Grid container>
            <Grid item sx={{mt:1}} color="primary.contrastText">
              <CheckCircleIcon color="success" sx={{fontSize: 18, pt: '4px'}} />&nbsp;I have read and agree to&nbsp;
              <Link component={RouteLink} to="/password-reset/" color="primary">
                Terms & Conditions
              </Link><br />&nbsp;and&nbsp;
              <Link component={RouteLink} to="/password-reset/" color="primary">
                Privacy Policy
              </Link>
            </Grid>
          </Grid>
          <Grid container justifyContent="center" >
            <Button
              disabled={isSiningUp}
              type="submit"
              variant="contained"
              sx={{ mt: 3, mb: 2, px: 3, textTransform: 'inherit' }}
              endIcon={isSiningUp ? <CircularProgress size={20} /> : <LoginIcon /> }
              size="large"
              >
              {isSiningUp ? 'Signing Up': (isOTPRequested ? 'Verify' : 'Sign Up')}
            </Button>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={9} sm={9} md={9} lg={9}>
        <Box sx={{ my: 2, textAlign: 'center'}}>Or</Box>
        <Box>
          Already account? <strong><Link component={RouteLink} to="/signin/" color="primary.contrastText">Sign In</Link></strong>
        </Box>
      </Grid>
    </Grid>
  );
}
